<script setup lang="ts">
import { computed, onUnmounted, reactive, watch } from 'vue';
import MoonPhaseCalculator from '@customily/moon-phase-calculator';
import ElementLibrarySelector from '@/components/DesignStudio/ElementLibrarySelector.vue';
import CInput from '@/components/shared/CInput.vue';
import CDatepicker from '@/components/shared/CDatepicker.vue';
import { LibraryTypeEnum } from '@/utils/enums/libraryType.enum';
import type { CanvasEx } from '@/typings/canvas';
import type {
    ActiveSelection,
    DynamicImage,
    ImagePlaceholderElement
} from '@/typings/customilyElements/canvasElements';
import type { ElementMapOption } from './types';
import type { KeyValueDto } from '@/typings/keyValueDto';
import type { LibraryDetailElement } from '@/typings/libraries';
import { buildCloudFrontUrl } from '@/utils/pathUtils';
import { limitImageResolutionFromFile, uploadImageToS3 } from '@/utils/imageUtils';
import { SignedUrlImageType } from '@/api/typings/image';
import { Provider } from '@/utils/PodProvider.enum';
import { runTasksInChunks } from '@/utils/promiseUtils';
import { getLibraryElementDetails } from '@/api/libraries';
import { previewFileForImagePlaceholder } from '@/utils/elementsUtils';

interface ImageFile {
    optionId: number;
    name: string;
}

type DynamicImagePath = ImagePlaceholderElement['dynamicImagesPath'];

const IMAGE_TYPE = LibraryTypeEnum.Image;

const props = defineProps<{
    canvas: CanvasEx;
    imageElement?: ImagePlaceholderElement;
    provider?: string;
    editMode?: boolean;
    isPreviewElement?: boolean;
    isProductBase?: boolean;
    previewMode?: boolean;
    maxImageSize?: number;
    selectedGroup?: ActiveSelection<ImagePlaceholderElement>;
}>();

const emit = defineEmits<{
    (e: 'open-image-library-selector'): void;
    (e: 'updated-product-colors', images: ImageFile[]): void;
    (e: 'trigger-loading', isLoading: boolean, msg?: string): void;
    (e: 'display-sample-option', element: ImagePlaceholderElement): void;
    (e: 'image-resized'): void;
}>();

const model = reactive({
    elementId: undefined as number | undefined,
    imageOption: 0 as number | undefined,
    imageMap: new Map<number, DynamicImage>(),
    imageLibraryId: undefined as number | undefined,
    moonPhaseDate: undefined as Date | undefined,
    isMoonPhase: false,
    isPrintArea: false,
    isLibraryIdMixed: false,
    isImageMapMixed: false,
    isElementIdMixed: false
});

const disabled = computed(() => !!props.imageElement?.locked);
const readonlyMode = computed(() => props.previewMode && props.isProductBase && props.imageElement?.isPrintArea);

watch(
    () => props.imageElement,
    (element) => {
        if (!element || !element.dynamicImagesPath) return;

        model.elementId = element.id;
        model.imageOption = element.dynamicImageVariation || 1;
        model.imageLibraryId = element.imageLibraryId;
        model.isPrintArea = element.isPrintArea || false;
        model.isMoonPhase = element.isMoonPhase || false;
        model.moonPhaseDate = element.isMoonPhase ? new Date() : undefined;
        initImageMap(element.dynamicImagesPath);
    }
);

const initImageMap = (images: DynamicImagePath) => {
    const newMap = new Map<number, DynamicImage>();
    for (const [position, image] of [...images]) {
        if (!image) continue;

        if (typeof image === 'string') {
            newMap.set(position, { path: image, fileName: '' });
            continue;
        }

        newMap.set(position, image);
    }
    model.imageMap = new Map(newMap);
};

const isImageMapMixed = (
    firstMap: ImagePlaceholderElement['dynamicImagesPath'],
    secondMap: ImagePlaceholderElement['dynamicImagesPath']
): boolean => {
    if (firstMap.size !== secondMap.size) return true;
    return [...firstMap.entries()].some(([key, value]) => {
        const mapValue = secondMap.get(key);
        return (
            mapValue &&
            (typeof value === 'string' ? value !== mapValue : value.path !== (mapValue as DynamicImage).path)
        );
    });
};

watch(
    () => props.selectedGroup,
    (group) => {
        const objects = group?.getObjects();
        if (!objects?.length) return;

        const firstElement = objects[0];
        model.imageOption = firstElement.dynamicImageVariation || 1;
        for (const element of objects) {
            if (model.isImageMapMixed && model.isLibraryIdMixed && model.isElementIdMixed) break;

            if (element.id !== firstElement.id) {
                model.isElementIdMixed = true;
            }

            if (element.imageLibraryId !== firstElement.imageLibraryId) {
                model.isLibraryIdMixed = true;
            }

            if (!model.isImageMapMixed) {
                model.isImageMapMixed = isImageMapMixed(firstElement.dynamicImagesPath, element.dynamicImagesPath);
            }

            if (model.imageOption !== element.dynamicImageVariation) {
                model.imageOption = undefined;
            }
        }

        if (!model.isElementIdMixed) model.elementId = firstElement.id;
        if (!model.isLibraryIdMixed) model.imageLibraryId = firstElement.imageLibraryId;
        if (!model.isImageMapMixed) initImageMap(firstElement.dynamicImagesPath);
    },
    { immediate: true }
);

const isManualPodTemplate = computed(
    () => ![Provider.PRINTFUL as string, Provider.PRINTIFY as string].includes(props.provider || '')
);

const displayLoader = (active = true, message?: string) => emit('trigger-loading', active, message);

const onImageUpload = async (files: File[], position?: number) => {
    const element = props.imageElement || props.selectedGroup?.getObjects()[0];
    if (!element) return;

    const imageFiles: ImageFile[] = [];
    let resized = false;
    let imgPosition = position || model.imageMap.size + 1;
    const dynamicImagesPath = new Map(model.isImageMapMixed ? [] : element.dynamicImagesPath);

    try {
        displayLoader(true, 'Uploading images...');

        for (const imageFile of files) {
            let file = imageFile;
            let locationPath: SignedUrlImageType = 'preview';

            if (props.previewMode) {
                const result = await limitImageResolutionFromFile(file, props.maxImageSize!, props.maxImageSize!);
                file = result.file;
                resized = resized || result.resized;
            }

            await previewFileForImagePlaceholder(props.canvas, element, file);

            if (element.isOverlay) {
                locationPath = 'preview-mask';
            } else if (element.isColorOptions) {
                locationPath = 'product-bases';
            }

            const image = await uploadImageToS3(locationPath, file);
            const filename = file.name.substring(0, file.name.lastIndexOf('.'));

            let dynamicImage: string | DynamicImage = image.Path;

            if (element.isColorOptions && isManualPodTemplate.value) {
                dynamicImage = { path: image.Path, fileName: filename };
                imageFiles.push({
                    optionId: imgPosition,
                    name: file.name
                });
            }

            dynamicImagesPath.set(imgPosition, dynamicImage);
            imgPosition++;
        }

        initImageMap(dynamicImagesPath);
        emit('updated-product-colors', imageFiles);
        await updateElement((el) => {
            if (model.isImageMapMixed) el.dynamicImagesPath.clear();

            for (const [position, path] of dynamicImagesPath.entries()) {
                el.dynamicImagesPath.set(position, path);
            }
            return loadImageIntoElement(imgPosition - 1, undefined, el);
        });
        model.isImageMapMixed = false;

        if (resized && props.previewMode) {
            emit('image-resized');
        }
    } catch (error) {
        console.error(error);
    } finally {
        displayLoader(false);
    }
};

const updateElement = async (
    callback: (element: ImagePlaceholderElement, list?: ImagePlaceholderElement[]) => void | Promise<void>
) => {
    const promises: Promise<void>[] = [];
    const objects = props.selectedGroup ? props.selectedGroup.getObjects() : [props.imageElement!];

    for (const element of objects) {
        if (!element) continue;
        promises.push(Promise.resolve(callback(element, objects)));
    }
    await runTasksInChunks(promises);
};

const cachedLibElements = new Map<string, LibraryDetailElement[]>();
const onLoadOption = async (option: number, value?: ElementMapOption) => {
    updateElement(async (el, list) => {
        let val = value;
        if (!list || list.length < 2) {
            return loadImageIntoElement(option, val, el);
        }

        if (el.imageLibraryId) {
            const id = `${el.imageLibraryId}-${option}`;
            const libElement =
                cachedLibElements.get(id) ||
                (await getLibraryElementDetails(el.imageLibraryId as number, IMAGE_TYPE, undefined, '', option));
            if (!libElement.length) return;
            cachedLibElements.set(id, libElement);
            val = libElement[0].path;
        } else {
            val = el.dynamicImagesPath.get(option);
        }
        return loadImageIntoElement(option, val, el);
    });
};

const loadImageIntoElement = async (option: number, value?: ElementMapOption, el?: ImagePlaceholderElement) => {
    const element = el || props.imageElement;
    if (!element) return;

    const image = typeof value == 'string' ? value : (value as DynamicImage)?.path || model.imageMap.get(option)?.path;

    if (!image) {
        element.dynamicImageVariation = model.imageOption = 1;
        emit('display-sample-option', element);
        return;
    }

    element.dynamicImageVariation = option;
    model.imageOption = option;
    await fetchFabricImage(element, image).catch((error) => {
        console.warn('An error occurred while loading the image', error);
        emit('display-sample-option', element);
    });
};

const fetchFabricImage = (element: ImagePlaceholderElement, image: string) => {
    return new Promise<void>((resolve, reject) => {
        window.fabric.util.loadImage(
            buildCloudFrontUrl(image),
            (img) => {
                if (!img) {
                    reject(`Failed to load image: ${image}`);
                }

                element.setElement(img);
                props.canvas.requestRenderAll();
                resolve();
            },
            null,
            'Anonymous'
        );
    });
};

const removeImageOptionFromMap = (position: number) => {
    const oldMap = model.imageMap;
    updateElement((element) => {
        element.dynamicImagesPath.delete(position);
        return loadImageIntoElement(1, undefined, element);
    });
    oldMap.delete(position);
    model.imageMap = new Map(oldMap);
};

const onImageOptionsReordered = async (options: KeyValueDto<number, ElementMapOption>[]) => {
    const element = props.imageElement || props.selectedGroup?.getObjects()[0];
    if (!element) return;

    const imageMap: DynamicImagePath = new Map();
    const imageFiles: ImageFile[] = [];

    options.forEach(({ value }, index) => {
        const position = index + 1;
        let _value: string | DynamicImage;
        if (element.isColorOptions) {
            const image = value as DynamicImage;
            _value = image;
            imageFiles.push({
                optionId: position,
                name: image.fileName
            });
        } else if (!element.isColorOptions && typeof value != 'string') {
            _value = (value as DynamicImage)?.path ?? value;
        } else {
            _value = value as string;
        }

        imageMap.set(position, _value);
    });

    initImageMap(imageMap);
    await updateElement((el) => {
        el.dynamicImagesPath = imageMap;
        return loadImageIntoElement(1, undefined, el);
    });
    emit('updated-product-colors', imageFiles);
};

const removeImageLibrary = () => {
    updateElement((element) => {
        element.imageLibraryId = undefined;
        if (props.editMode) {
            element.setEditedProp('imageLibraryId');
        }
        return loadImageIntoElement(1, undefined, element);
    });
    model.imageLibraryId = undefined;
    model.isLibraryIdMixed = false;
};

const updateElementId = () => {
    updateElement((element) => {
        element.id = model.elementId as number;
    });
    model.isElementIdMixed = false;
};

const onMoonPhaseDateChanged = (date?: Date) => {
    if (!date) return;
    const formatted = new Date(date);
    formatted.setHours(12, 0, 0, 0);
    const phase = MoonPhaseCalculator.handleCalculateMoonPhase(formatted);
    onLoadOption(phase);
};

watch(
    () => props.canvas,
    (canvas) => {
        if (!canvas) return;
        canvas.on('images:pasted', onImageUpload);
    },
    { immediate: true }
);

onUnmounted(() => {
    props.canvas.off('images:pasted', onImageUpload);
});

const currentSlot = computed(() => (model.imageLibraryId ? 'header' : 'actions'));
const elementIdPlaceholder = computed(() => (model.isElementIdMixed ? 'Mixed' : '1'));
</script>

<template>
    <div class="dynamic-image-properties-wrapper">
        <element-library-selector
            :disabled="disabled"
            :readonly="readonlyMode"
            :hide-library-selection="model.isPrintArea"
            :readonly-options="model.isMoonPhase"
            :element-list="model.imageMap"
            :library-id="model.imageLibraryId"
            :library-type="IMAGE_TYPE"
            :selected-position="model.imageOption"
            :mixed-library="model.isLibraryIdMixed"
            :mixed-element-list="model.isImageMapMixed"
            :element-list-position="3"
            @files-uploaded="onImageUpload"
            @load-option="onLoadOption"
            @options-reordered="onImageOptionsReordered"
            @remove-map-option="removeImageOptionFromMap"
            @remove-library="removeImageLibrary"
            @pick-library="emit('open-image-library-selector')"
        >
            <template :slot="currentSlot">
                <c-input
                    v-model="model.elementId"
                    class="w-25 no-fill"
                    label="Element ID"
                    type="number"
                    :min="1"
                    :placeholder="elementIdPlaceholder"
                    :disabled="disabled || (previewMode && !isPreviewElement)"
                    :pattern="/^[1-9]\d*$/"
                    @input="updateElementId"
                />
                <c-datepicker
                    v-if="model.isMoonPhase"
                    v-model="model.moonPhaseDate"
                    class="w-25 no-fill"
                    label="Type or select a date"
                    placeholder="Enter date"
                    :disabled="disabled"
                    @input="onMoonPhaseDateChanged"
                />
            </template>
        </element-library-selector>
    </div>
</template>
